import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { usePageTitle } from "@/hooks/use-page-title";
import { useMemo, useState } from "react";
import { motion, MotionConfig } from "motion/react";
import {
  ArrowLeft,
  Users,
  Link2,
  Loader2,
  CreditCard,
  Zap,
  Check,
  X,
  AlertTriangle,
  History,
  ExternalLink,
  CalendarDays,
  Clock,
  Server,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { IconContainer } from "@/components/ui/icon-container";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MembersList, InviteDialog, InvitesList } from "@/components/teams";
import {
  useSubscription,
  useCreatePortalSession,
  dashboardMeQueryOptions,
  teamMembersQueryOptions,
  teamInvitesQueryOptions,
  useTeamAuditLog,
} from "@/lib/api";
import type {
  SubscriptionInfo,
  DashboardApplicationGuild,
} from "@/lib/api/types";
import { useQueries } from "@tanstack/react-query";
import { AuditLogList } from "@/components/audit-log/audit-log-list";
import {
  TeamPageSkeleton,
  SubscriptionTabSkeleton,
  MembersTabSkeleton,
} from "@/components/teams/team-skeleton";
import { getDiscordIconUrl } from "@/utils/discord";

type tenantsTab = "members" | "invites" | "subscription" | "activity";

export const Route = createFileRoute("/dashboard/teams/$tenantId")({
  component: TeamManagementPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as tenantsTab) || "members",
  }),
});

function UsageBar({
  label,
  current,
  max,
  note,
}: {
  label: string;
  current: number;
  max: number;
  note?: string;
}) {
  const percentage = Math.min((current / max) * 100, 100);
  const isNearLimit = percentage >= 80;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground text-sm">{label}</span>
          {note && (
            <span className="text-muted-foreground/50 text-xs">({note})</span>
          )}
        </div>
        <span
          className={`text-sm ${isNearLimit ? "font-medium text-rose-500" : "text-muted-foreground"}`}
        >
          <span className="tabular-nums">{current.toLocaleString()}</span>
          <span className="mx-0.5 opacity-40">/</span>
          <span className="tabular-nums">{max.toLocaleString()}</span>
        </span>
      </div>
      <Progress value={percentage} />
    </div>
  );
}

function SubscriptionTab({
  tenantId,
  tenantName,
  isOwner,
  guilds,
}: {
  tenantId: string;
  tenantName: string;
  isOwner: boolean;
  guilds: DashboardApplicationGuild[];
}) {
  const navigate = useNavigate();
  const [selectedGuildId, setSelectedGuildId] = useState<string | undefined>();
  const {
    data: subData,
    isPending,
    isFetching,
  } = useSubscription(tenantId, selectedGuildId);
  const currentPlan = (subData?.data as SubscriptionInfo | undefined)?.plan;
  const isPaidPlan = currentPlan === "pro" || currentPlan === "unlimited";
  const hasStripeSubscription =
    (subData?.data as SubscriptionInfo | undefined)?.hasStripeSubscription ??
    false;
  const canUpgrade = isOwner && !isPaidPlan;
  const portalSession = useCreatePortalSession(tenantId);

  if (isPending && !subData) {
    return <SubscriptionTabSkeleton />;
  }

  const sub = subData?.data as SubscriptionInfo | undefined;
  if (!sub) {
    return (
      <Card>
        <CardContent className="text-muted-foreground py-12 text-center">
          No subscription data available.
        </CardContent>
      </Card>
    );
  }

  const features = [
    { label: "Knowledge Base", enabled: sub.features.knowledgeBase },
    { label: "Ticket System", enabled: sub.features.ticketSystem },
    { label: "Custom Bot", enabled: sub.features.customBot },
    { label: "Ticket Transcripts", enabled: sub.features.ticketTranscripts },
    { label: "Ticket Claiming", enabled: sub.features.ticketClaiming },
    { label: "Ticket AI", enabled: sub.features.ticketAI },
    { label: "Modmail", enabled: sub.features.modmail },
    { label: "Idle Tickets", enabled: sub.features.idleTickets },
    { label: "Thread Q&A", enabled: sub.features.threadsQA },
  ];

  const enabledFeatures = features.filter((f) => f.enabled);
  const disabledFeatures = features.filter((f) => !f.enabled);

  return (
    <div className="space-y-4">
      {/* Status banner */}
      {currentPlan !== "unlimited" && sub.status !== "active" && (
        <div
          className={`flex items-center gap-3 rounded-xl border p-4 ${
            sub.status === "past_due"
              ? "border-amber-500/30 bg-amber-500/5 text-amber-600 dark:text-amber-400"
              : "border-rose-500/30 bg-rose-500/5 text-rose-600 dark:text-rose-400"
          }`}
        >
          <AlertTriangle className="size-5 shrink-0" />
          <p className="text-pretty text-sm">
            {sub.status === "past_due"
              ? "Your payment is past due. Please update your payment method to avoid service interruption."
              : sub.currentPeriodEnd &&
                  new Date(sub.currentPeriodEnd) > new Date()
                ? `Your subscription has been canceled. You still have access until ${new Date(sub.currentPeriodEnd).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}.`
                : "Your subscription has been canceled. You have been downgraded to the Free plan."}
          </p>
        </div>
      )}

      {/* Plan card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold">
                {sub.planName}
              </h2>
              <p className="text-muted-foreground mt-0.5 text-sm">
                {tenantName}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {currentPlan !== "unlimited" && sub.status !== "active" && (
                <Badge
                  variant="outline"
                  className={
                    sub.status === "past_due"
                      ? "border-amber-500/50 text-amber-600"
                      : "border-rose-500/50 text-rose-600"
                  }
                >
                  {sub.status === "past_due" ? "Past Due" : "Canceled"}
                </Badge>
              )}
              <Badge
                variant={isPaidPlan ? "default" : "secondary"}
                className={
                  isPaidPlan
                    ? "border-0 bg-rose-500 text-white hover:bg-rose-600"
                    : ""
                }
              >
                {sub.planName}
              </Badge>
            </div>
          </div>

          {/* Billing metadata */}
          {currentPlan !== "unlimited" &&
            hasStripeSubscription &&
            (sub.currentPeriodEnd || sub.canceledAt) && (
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t pt-4">
                {sub.currentPeriodEnd && (
                  <div className="flex items-center gap-2 text-sm">
                    <CalendarDays className="text-muted-foreground size-3.5" />
                    <span className="text-muted-foreground">
                      {sub.status === "canceled"
                        ? "Access until"
                        : "Next billing"}
                    </span>
                    <span className="font-medium tabular-nums">
                      {new Date(sub.currentPeriodEnd).toLocaleDateString(
                        undefined,
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        }
                      )}
                    </span>
                  </div>
                )}
                {sub.canceledAt && (
                  <div className="flex items-center gap-2 text-sm">
                    <AlertTriangle className="text-muted-foreground size-3.5" />
                    <span className="text-muted-foreground">Canceled</span>
                    <span className="font-medium tabular-nums">
                      {new Date(sub.canceledAt).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                )}
                {sub.status === "canceled" &&
                  sub.currentPeriodEnd &&
                  new Date(sub.currentPeriodEnd) > new Date() && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="text-muted-foreground size-3.5" />
                      <span className="text-muted-foreground">Remaining</span>
                      <span className="font-medium tabular-nums text-amber-600 dark:text-amber-400">
                        {Math.ceil(
                          (new Date(sub.currentPeriodEnd).getTime() -
                            Date.now()) /
                            (1000 * 60 * 60 * 24)
                        )}{" "}
                        days
                      </span>
                    </div>
                  )}
              </div>
            )}

          {/* Actions */}
          {currentPlan !== "unlimited" && (
            <div className="mt-5 flex items-center gap-2">
              {canUpgrade && (
                <Button
                  onClick={() =>
                    navigate({
                      to: "/dashboard/checkout",
                      search: { tenantId, plan: "pro" },
                    })
                  }
                  className="bg-rose-500 text-white hover:bg-rose-600"
                >
                  <Zap className="mr-2 size-4" />
                  Upgrade to Pro
                </Button>
              )}
              {currentPlan === "pro" && isOwner && hasStripeSubscription && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => portalSession.mutate()}
                  disabled={portalSession.isPending}
                >
                  {portalSession.isPending ? (
                    <Loader2 className="mr-2 size-3.5 animate-spin" />
                  ) : (
                    <ExternalLink className="mr-2 size-3.5" />
                  )}
                  Manage Billing
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardContent className="p-6">
          <h3 className="font-heading mb-4 text-sm font-semibold">
            Plan Features
          </h3>
          <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
            {enabledFeatures.map((f) => (
              <div key={f.label} className="flex items-center gap-2.5 py-0.5">
                <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
                  <Check className="size-3 text-emerald-500" />
                </div>
                <span className="text-sm">{f.label}</span>
              </div>
            ))}
            {disabledFeatures.map((f) => (
              <div key={f.label} className="flex items-center gap-2.5 py-0.5">
                <div className="bg-muted flex size-5 shrink-0 items-center justify-center rounded-full">
                  <X className="text-muted-foreground/40 size-3" />
                </div>
                <span className="text-muted-foreground/60 text-sm">
                  {f.label}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Usage */}
      <Card>
        <CardContent className="p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-sm font-semibold">Usage</h3>
              {isFetching && (
                <Loader2 className="text-muted-foreground size-3.5 animate-spin" />
              )}
            </div>
            {guilds.length > 0 && (
              <Select
                value={selectedGuildId ?? "all"}
                onValueChange={(v) =>
                  setSelectedGuildId(v === "all" ? undefined : v)
                }
              >
                <SelectTrigger className="h-8 w-[200px] text-xs">
                  <SelectValue placeholder="All guilds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="flex items-center gap-2">
                      <Server className="text-muted-foreground size-3.5" />
                      All guilds
                    </span>
                  </SelectItem>
                  {guilds.map((guild) => {
                    const iconUrl = getDiscordIconUrl(
                      guild.discordGuildId,
                      guild.discordGuildIcon
                    );
                    return (
                      <SelectItem key={guild.id} value={guild.id}>
                        <span className="flex items-center gap-2">
                          {iconUrl ? (
                            <img
                              src={iconUrl}
                              alt=""
                              className="size-4 shrink-0 rounded-full"
                            />
                          ) : (
                            <div className="bg-muted flex size-4 shrink-0 items-center justify-center rounded-full">
                              <span className="text-muted-foreground text-[8px] font-medium">
                                {guild.discordGuildName.charAt(0)}
                              </span>
                            </div>
                          )}
                          <span className="truncate">
                            {guild.discordGuildName}
                          </span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-4">
            <UsageBar
              label="Guilds"
              current={sub.usage.guilds}
              max={sub.limits.maxGuildsPerApplication}
            />
            <UsageBar
              label="Documents per Guild"
              current={sub.usage.documents}
              max={sub.limits.maxDocumentsPerGuild}
              note={!sub.isGuildScoped ? "select a guild" : undefined}
            />
            <UsageBar
              label="AI Messages (this month)"
              current={sub.usage.aiMessagesThisMonth}
              max={sub.limits.maxAiMessagesPerMonth}
            />
            <UsageBar
              label="Ticket Categories per Guild"
              current={sub.usage.ticketCategories}
              max={sub.limits.maxTicketCategoriesPerGuild}
              note={!sub.isGuildScoped ? "select a guild" : undefined}
            />
            <UsageBar
              label="Team Members"
              current={sub.usage.teamMembers}
              max={sub.limits.maxTeamMembers}
            />
            <UsageBar
              label="Thread Q&A Configs per Guild"
              current={sub.usage.threadQAConfigs}
              max={sub.limits.maxThreadQAConfigsPerGuild}
              note={!sub.isGuildScoped ? "select a guild" : undefined}
            />
            <UsageBar
              label="Custom Bots"
              current={sub.usage.customBots}
              max={sub.limits.maxCustomBots}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ActivityTab({ tenantId }: { tenantId: string }) {
  const { data, isPending } = useTeamAuditLog(tenantId);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <IconContainer icon={History} variant="muted" />
          <div>
            <CardTitle className="text-base">Activity Log</CardTitle>
            <CardDescription>
              Recent actions across your team and guilds.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <AuditLogList
          entries={data?.data ?? []}
          isLoading={isPending}
          emptyMessage="No activity recorded yet."
        />
      </CardContent>
    </Card>
  );
}

function TeamManagementPage() {
  const { tenantId } = Route.useParams();
  const { tab } = Route.useSearch();
  const { session } = Route.useRouteContext();
  const navigate = useNavigate();

  const [
    { data: dashboardData, isPending: dashboardPending },
    { data: membersData, isPending: membersPending },
    { data: invitesData, isPending: invitesPending },
  ] = useQueries({
    queries: [
      dashboardMeQueryOptions,
      teamMembersQueryOptions(tenantId),
      teamInvitesQueryOptions(tenantId),
    ],
  });

  const tenantName = dashboardData?.data?.tenants.find(
    (t) => t.id === tenantId
  )?.name;

  usePageTitle(tenantName ? `${tenantName} Team` : "Team");

  const tenantGuilds = useMemo(() => {
    const seen = new Map<string, DashboardApplicationGuild>();
    for (const app of dashboardData?.data.applications ?? []) {
      if (app.tenant.id !== tenantId) continue;
      for (const guild of app.guilds) {
        if (!seen.has(guild.id)) seen.set(guild.id, guild);
      }
    }
    return [...seen.values()];
  }, [dashboardData, tenantId]);

  if (dashboardPending) {
    return <TeamPageSkeleton />;
  }

  const tenant = dashboardData?.data.tenants.find((t) => t.id === tenantId);

  if (!tenant) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild className="gap-1.5">
          <Link to="/dashboard">
            <ArrowLeft className="size-4" />
            Back to Dashboard
          </Link>
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <IconContainer
              icon={Users}
              size="lg"
              variant="muted"
              className="mb-3"
            />
            <p className="text-muted-foreground">
              Team not found or you don&apos;t have access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canManage = tenant.role === "owner" || tenant.role === "admin";

  if (!canManage) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild className="gap-1.5">
          <Link to="/dashboard">
            <ArrowLeft className="size-4" />
            Back to Dashboard
          </Link>
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <IconContainer
              icon={Users}
              size="lg"
              variant="muted"
              className="mb-3"
            />
            <p className="text-muted-foreground">
              You don&apos;t have permission to manage this team.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="space-y-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              asChild
              aria-label="Back to dashboard"
            >
              <Link to="/dashboard">
                <ArrowLeft className="size-4" />
              </Link>
            </Button>
            <div className="flex items-center gap-3">
              <IconContainer icon={Users} variant="accent" />
              <div>
                <h1 className="text-balance text-2xl font-semibold">
                  {tenant.name}
                </h1>
                <p className="text-muted-foreground text-pretty text-sm">
                  Team management
                </p>
              </div>
            </div>
          </div>
          <InviteDialog tenantId={tenantId} currentUserRole={tenant.role} />
        </div>

        <Tabs
          value={tab}
          onValueChange={(value) =>
            navigate({
              to: "/dashboard/teams/$tenantId",
              params: { tenantId },
              search: { tab: value as tenantsTab },
              replace: true,
            })
          }
        >
          <TabsList>
            <TabsTrigger value="members" className="gap-1.5">
              <Users className="size-4" />
              Members
            </TabsTrigger>
            <TabsTrigger value="invites" className="gap-1.5">
              <Link2 className="size-4" />
              Invitations
            </TabsTrigger>
            <TabsTrigger value="subscription" className="gap-1.5">
              <CreditCard className="size-4" />
              Subscription
            </TabsTrigger>
            {tenant.role === "owner" && (
              <TabsTrigger value="activity" className="gap-1.5">
                <History className="size-4" />
                Activity
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Team members</CardTitle>
              </CardHeader>
              <CardContent>
                {membersPending ? (
                  <MembersTabSkeleton />
                ) : membersData?.data ? (
                  <MembersList
                    tenantId={tenantId}
                    members={membersData.data.members}
                    grouped={membersData.data.grouped}
                    currentUserRole={tenant.role}
                    currentUserId={session.user.id}
                  />
                ) : (
                  <p className="text-muted-foreground py-8 text-center text-sm">
                    Failed to load members
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invites">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Active invitations</CardTitle>
              </CardHeader>
              <CardContent>
                {invitesPending ? (
                  <MembersTabSkeleton />
                ) : invitesData?.data ? (
                  <InvitesList
                    tenantId={tenantId}
                    invites={invitesData.data.invites}
                  />
                ) : (
                  <p className="text-muted-foreground py-8 text-center text-sm">
                    Failed to load invitations
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscription">
            <SubscriptionTab
              tenantId={tenantId}
              tenantName={tenant.name}
              isOwner={tenant.role === "owner"}
              guilds={tenantGuilds}
            />
          </TabsContent>

          {tenant.role === "owner" && (
            <TabsContent value="activity">
              <ActivityTab tenantId={tenantId} />
            </TabsContent>
          )}
        </Tabs>
      </motion.div>
    </MotionConfig>
  );
}
